import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Plus, Image as ImageIcon, Type, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface MoodBoardItem {
  id: number;
  type: "image" | "note";
  content: string;
  title?: string;
  color?: string;
}

const DashboardMoodBoard = () => {
  const [items, setItems] = useState<MoodBoardItem[]>([
    {
      id: 1,
      type: "note",
      content: "Focus on building strong revenue foundations",
      title: "Business Goal",
      color: "bg-blue-500/20",
    },
    {
      id: 2,
      type: "note",
      content: "Consistency beats intensity every time",
      title: "Daily Reminder",
      color: "bg-green-500/20",
    },
  ]);

  const [newImageUrl, setNewImageUrl] = useState("");
  const [newNoteTitle, setNewNoteTitle] = useState("");
  const [newNoteContent, setNewNoteContent] = useState("");
  const [selectedColor, setSelectedColor] = useState("bg-purple-500/20");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const colors = [
    "bg-purple-500/20",
    "bg-blue-500/20",
    "bg-green-500/20",
    "bg-yellow-500/20",
    "bg-pink-500/20",
    "bg-orange-500/20",
  ];

  const addImage = () => {
    if (newImageUrl.trim()) {
      setItems([
        ...items,
        {
          id: Date.now(),
          type: "image",
          content: newImageUrl,
        },
      ]);
      setNewImageUrl("");
      setIsDialogOpen(false);
      toast.success("Image added to mood board!");
    }
  };

  const addNote = () => {
    if (newNoteContent.trim()) {
      setItems([
        ...items,
        {
          id: Date.now(),
          type: "note",
          content: newNoteContent,
          title: newNoteTitle || "Note",
          color: selectedColor,
        },
      ]);
      setNewNoteTitle("");
      setNewNoteContent("");
      setIsDialogOpen(false);
      toast.success("Note added to mood board!");
    }
  };

  const deleteItem = (id: number) => {
    setItems(items.filter((item) => item.id !== id));
    toast.success("Item removed from mood board");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4 py-12 max-w-7xl">
        <Link to="/dashboard">
          <Button variant="ghost" className="mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </Link>

        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Mood Board</h1>
              <p className="text-xl text-muted-foreground">
                Collect inspiration, ideas, and reminders to keep you motivated
              </p>
            </div>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button size="lg" className="gap-2">
                  <Plus className="w-5 h-5" />
                  Add Item
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>Add to Mood Board</DialogTitle>
                  <DialogDescription>
                    Add an image or note to your mood board for inspiration
                  </DialogDescription>
                </DialogHeader>

                <Tabs defaultValue="note" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="note">
                      <Type className="w-4 h-4 mr-2" />
                      Note
                    </TabsTrigger>
                    <TabsTrigger value="image">
                      <ImageIcon className="w-4 h-4 mr-2" />
                      Image
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="note" className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Title (Optional)</label>
                      <Input
                        placeholder="Enter note title..."
                        value={newNoteTitle}
                        onChange={(e) => setNewNoteTitle(e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Content</label>
                      <Textarea
                        placeholder="Enter your note content..."
                        value={newNoteContent}
                        onChange={(e) => setNewNoteContent(e.target.value)}
                        rows={4}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Color</label>
                      <div className="flex gap-2">
                        {colors.map((color) => (
                          <button
                            key={color}
                            onClick={() => setSelectedColor(color)}
                            className={`w-10 h-10 rounded-lg ${color} border-2 ${
                              selectedColor === color
                                ? "border-primary scale-110"
                                : "border-transparent"
                            } transition-all hover:scale-105`}
                          />
                        ))}
                      </div>
                    </div>

                    <DialogFooter>
                      <Button onClick={addNote}>Add Note</Button>
                    </DialogFooter>
                  </TabsContent>

                  <TabsContent value="image" className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Image URL</label>
                      <Input
                        placeholder="Enter image URL..."
                        value={newImageUrl}
                        onChange={(e) => setNewImageUrl(e.target.value)}
                      />
                    </div>

                    {newImageUrl && (
                      <div className="rounded-lg overflow-hidden border">
                        <img
                          src={newImageUrl}
                          alt="Preview"
                          className="w-full h-48 object-cover"
                          onError={() => toast.error("Invalid image URL")}
                        />
                      </div>
                    )}

                    <DialogFooter>
                      <Button onClick={addImage}>Add Image</Button>
                    </DialogFooter>
                  </TabsContent>
                </Tabs>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Mood Board Grid */}
        <div className="columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-6 space-y-6">
          {items.map((item) => (
            <Card
              key={item.id}
              className="break-inside-avoid group relative hover:shadow-xl transition-all duration-300 overflow-hidden"
            >
              <button
                onClick={() => deleteItem(item.id)}
                className="absolute top-2 right-2 z-10 p-2 rounded-lg bg-destructive/90 text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive"
              >
                <Trash2 className="w-4 h-4" />
              </button>

              {item.type === "image" ? (
                <img
                  src={item.content}
                  alt="Mood board item"
                  className="w-full h-auto object-cover"
                />
              ) : (
                <CardContent className={`p-6 ${item.color}`}>
                  {item.title && (
                    <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                  )}
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {item.content}
                  </p>
                </CardContent>
              )}
            </Card>
          ))}
        </div>

        {items.length === 0 && (
          <div className="text-center py-20">
            <div className="inline-flex p-6 rounded-full bg-primary/10 mb-6">
              <ImageIcon className="w-12 h-12 text-primary" />
            </div>
            <h3 className="text-2xl font-bold mb-2">Your Mood Board is Empty</h3>
            <p className="text-muted-foreground mb-6">
              Start adding images and notes to create your inspiration board
            </p>
            <Button onClick={() => setIsDialogOpen(true)} size="lg">
              <Plus className="w-5 h-5 mr-2" />
              Add Your First Item
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardMoodBoard;
